import loadable from '@loadable/component';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { default as Typography } from '@mui/material/Typography';
import { Link } from 'gatsby';
import React from 'react';
import tagProduct from '../queries/tagProducts';
import If from './If';
import SimpleCollection from './SimpleCollection';
import { Title, TypeOcrH6 } from './Types';

const Select = loadable(() => import('@mui/material/Select'));

const SORT_AZ = 'Alphabetically, A-Z';
const SORT_ZA = 'Alphabetically, Z-A';
const SORT_LH = 'Price, low to high';
const SORT_HL = 'Price, high to low';

interface HpFilterProps {
  hpFilter: string;
  setHpFilter: (a: string) => void;
  hps: string[];
}
function HpFilter(props: HpFilterProps) {
  return (
    <div>
      <Select
        value={props.hpFilter}
        onChange={(e: SelectChangeEvent) => {
          props.setHpFilter(e.target.value);
        }}
        sx={{ borderRadius: 0, height: '2rem', width: '6rem' }}
      >
        {props.hps.map((a: string) => {
          return (
            <MenuItem value={a} key={a}>
              {a}
            </MenuItem>
          );
        })}
      </Select>
    </div>
  );
}

interface SortFilterProps {
  sort: string;
  setSort: (a: string) => void;
}
function SortFilter(props: SortFilterProps) {
  return (
    <div>
      <Select
        value={props.sort}
        onChange={(e: SelectChangeEvent) => {
          props.setSort(e.target.value);
        }}
        sx={{ borderRadius: 0, height: '2rem', width: '15rem' }}
      >
        <MenuItem value={SORT_AZ}>{SORT_AZ}</MenuItem>
        <MenuItem value={SORT_ZA}>{SORT_ZA}</MenuItem>
        <MenuItem value={SORT_LH}>{SORT_LH}</MenuItem>
        <MenuItem value={SORT_HL}>{SORT_HL}</MenuItem>
      </Select>
    </div>
  );
}

function NewCollection() {
  return (
    <>
      What's new at Noise?
      <br></br>
      <br></br>
      We work hard to bring you new fun things regularly. Check out our most
      recent releases here.
    </>
  );
}

function OscillatorCollection() {
  return (
    <>
      Sound starts here.
      <br></br>
      <br></br> Sounds for all your needs, from industrial and techno to
      ambient, hiphop, pop, EDM or anything your sound heart desires. Dial in
      lush and soft timbres, incredible beats, industrial joy, gnarly basses, or
      grab our complex oscillators for lasers and space farts. With Noise
      Engineering digital oscillators, the world is your sound oyster.
    </>
  );
}

function DistortionCollection() {
  return (
    <>
      Gritty frequency enhancement
      <br></br>
      <br></br>
      Distortion is one of the basic food groups here at Noise Engineering.
      Whether you enjoy the gentle warmth of saturation or the stormy thunder
      end of the spectrum, we have those and all of the tonestacks in between.
    </>
  );
}

function EffectsCollection() {
  return (
    <>
      Reverb, distortion, and much much more.
      <br></br>
      <br></br>
      Looking for a beautiful Eurorack reverb? Need some distortion to beef up
      your techno kicks? Want a new analog filter to modify your sounds? Look no
      further than the NE effects section!
    </>
  );
}

function UtilityCollection() {
  return (
    <>
      Make your modules work for you!
      <br></br>
      <br></br>
      Utilities: the secret to making your patches function for you. Whether you
      need clock multiplication and division, CV attenuation and offset, or a
      new tactile control for your performances, look no further than Noise
      Engineering utilities.
    </>
  );
}

function SequencingCollection() {
  return (
    <>
      Bring your rack to life.
      <br></br>
      <br></br>
      Eurorack sequencers are the heart of many a patch. Generate CV for
      melodies and modulation, explore math-based algorithmic rhythm generation,
      or just jam with tactile modules optimized for live performance. Our
      sequencers are fun, immediate, and interactive ways to play with your
      system.
    </>
  );
}
function FiveUCollection() {
  return (
    <>
      When 3U of sound just isn't enough.
      <br></br>
      <br></br>
      Noise Engineering oscillators now in MU format. Compatible with all
      synthesizers.com cabinets, get these unique sounds in your rack!
    </>
  );
}

function ModulesCollection() {
  return (
    <>
      Modular synths with just the right amount of opinion.
      <br></br>
      <br></br>
      We make our Eurorack modules with the tactile feel of live performance in
      mind, but they are a true stage/studio hybrid: analog sound with digital
      precision. Skiff friendly. Unparalleled sound spanning techno to ambient
      to hip hop to pop.
      <br></br>
      <br></br>From Ampla to Zularic, we have something for you.
    </>
  );
}

function AccessoriesCollection() {
  return (
    <>
      High fashion and modular extras.
      <br></br>
      <br></br>
      Whether you're looking for a new outfit for your module or for the attire
      to look great while making your next patch, we have you covered.
    </>
  );
}

function PanelsCollection() {
  return (
    <>
      Panels and overlays galore!
      <br></br>
      <br></br>
      Suit up your platform module with a hot-swappable overlay, or just grab a
      panel of a different color.
    </>
  );
}

function ClothingCollection() {
  return (
    <>
      High fashion is how we roll.
      <br></br>
      <br></br>
      Tell the world that you're one of the cool kids with Noise Engineering
      apparel.
    </>
  );
}

function SoftwareCollection() {
  return (
    <>
      What's in the box? NE!
      <br></br>
      <br></br>
      Now in VST, AU, AAX, and Rack Extensions for Reason. Check out our plugin
      collection here, including a bundle of free plugins for VST / AU / AAX!
      Bring the Noise Engineering quality of sound to your DAW.
      <br></br>
      <br></br>
      Just need to update? Grab the latest installer at&nbsp;
      <a href='https://noiseengineering.us/portal/plugins/'>
        noiseengineering.us/portal/firmware
      </a>
    </>
  );
}

function SynthCollection() {
  return (
    <>
      Get that Noise into the box.
      <br></br>
      <br></br>
      Noise Engineering synths in VST, AU, AAX, and Rack Extensions for Reason
      Bring the sound of Noise Engineering to your DAW. Mix and match three
      plugins for bundle pricing.
      <br></br>
      <br></br>
      Just need to update? Grab the latest installer at
      <a href='noiseengineering.us/portal/plugins'>
        noiseengineering.us/portal/plugins
      </a>
    </>
  );
}

function FxCollection() {
  return (
    <>
      Distort, reverberate, delay
      <br></br>
      <br></br>
      Noise Engineering effects in VST, AU, AAX, and Reason Rack Extensions. NE:
      get back in the box! Mix and match three plugins for bundle pricing.
      <br></br>
      <br></br>
      Just need to update? Grab the latest installer at{' '}
      <a href='https://noiseengineering.us/portal/plugins'>
        noiseengineering.us/portal/plugins
      </a>
    </>
  );
}

function BundleCollection() {
  return (
    <>
      Premade bundles for your purchasing ease.
      <br></br>
      <br></br>
      Noise Engineering effects in VST, AU, AAX, and Reason Rack Extensions.
      Each bundle was lovingly crafted for you, but build your own out of any
      three of our individual plugins!
      <br></br>
      <br></br>
      Just need to update? Grab the latest installer at
      <a href='noiseengineering.us/portal/plugins'>
        noiseengineering.us/portal/plugins
      </a>
    </>
  );
}

function PedalCollection() {
  return (
    <>
      Made for guitars, stellar on synths, drum machines, the occasional
      theremin, and more.
      <br></br>
      <br></br>
      Designed and built in Southern California, our pedals build on our decade
      of experience with unique effects in Eurorack for pedals that sound unlike
      anything on your board.
    </>
  );
}

function LegacyCollection() {
  return (
    <>
      Gone but not forgotten
      <br></br>
      <br></br>
      Find product info here for all of the modules we made once upon a time.
    </>
  );
}

function AliaCollection() {
  return (
    <>
      All of the Alia
      <br></br>
      Every module available on the Alia platform in one place.
      <br></br>
      Already have an Alia? Try any available firmware, free, anytime through
      the <Link to='/pages/world-of-alia'>World of Alia page </Link>{' '}
    </>
  );
}

function LegioCollection() {
  return (
    <>
      Legions of Legios!
      <br></br>
      Every module available on the Legio platform in one place.
      <br></br>
      Already have a Legio? Try any available firmware, free, anytime through
      the <Link to='/pages/world-of-legio'>World of Legio page </Link>{' '}
    </>
  );
}

function VersioCollection() {
  return (
    <>
      Versatile Versio!
      <br></br>
      Every module available on the Versio platform in one place.
      <br></br>
      Already have a Versio? Try any available firmware, free, anytime through
      the <Link to='/pages/world-of-versio'>World of Versio page </Link>{' '}
    </>
  );
}

function PitchCollection() {
  return (
    <>
      Turn your pitch-perfect dreams into a reality
      <br></br>
      <br></br>
      Randomized or preprogrammed and everything in between: modulate, sequence,
      and automate your patches with creative and unique sequencing utilities.
    </>
  );
}

function PlatformCollection() {
  return (
    <>
      All the firmwares, all the platforms
      <br></br>
      <br></br>
      Noise Engineering platform modules are versatility in a small footprint:
      buy one, get all the firmwares for that platform, free.
    </>
  );
}

function AmbientCollection() {
  return (
    <>
      The softer side of Noise Engineering: modules for generative and ambient.
      From procedural sequencers to drones to delicate voices to effect
      processing, we have what you need for your ambient set.
    </>
  );
}
function TechnoCollection() {
  return (
    <>
      More unce for the ounce. Get your techno on with Noise Engineering
      modules.
    </>
  );
}
function CinematicCollection() {
  return (
    <>
      From a Hans Zimmer BOMMMMMM to broad, sweeping, lush landscapes, Noise
      Engineering modules are here to help you create your next cinematic score.
    </>
  );
}
function GlitchCollection() {
  return (
    <>
      From a little glitchy embellishment to a full-on glitch set, we have
      modules for you.
    </>
  );
}
function IndustrialCollection() {
  return (
    <>
      Industrial music has been one of our greatest influences. Perhaps you can
      tell.
    </>
  );
}

function ModuleCollection() {
  return <>Product bundles made just for you at a discounted price.</>;
}

const headers: any = {
  oscillators: OscillatorCollection,
  distortion: DistortionCollection,
  effects: EffectsCollection,
  utilities: UtilityCollection,
  sequencing: SequencingCollection,
  accessories: AccessoriesCollection,
  software: SoftwareCollection,
  '5u': FiveUCollection,
  modules: ModulesCollection,
  panels: PanelsCollection,
  clothing: ClothingCollection,
  synths: SynthCollection,
  fx: FxCollection,
  bundles: BundleCollection,
  pedals: PedalCollection,
  legacy: LegacyCollection,
  new: NewCollection,
  alia: AliaCollection,
  legio: LegioCollection,
  versio: VersioCollection,
  pitch: PitchCollection,
  platform: PlatformCollection,
  ambient: AmbientCollection,
  techno: TechnoCollection,
  cinematic: CinematicCollection,
  glitch: GlitchCollection,
  industrial: IndustrialCollection,
  bundle: ModuleCollection,
};

interface CollectionProps {
  name: string;
  seo?: string;
}

export default function ProductCollection(props: CollectionProps) {
  const [hpFilter, setHpFilterState] = React.useState('All');

  const [sort, setSortState] = React.useState(SORT_AZ);

  const includes = props.name ? [props.name] : [];
  const allProds = tagProduct(
    includes,
    ['Legacy'],
    hpFilter === 'All' ? [] : [hpFilter]
  );

  const tags: any = {};
  for (const x of allProds) {
    for (const y of x.tags) {
      tags[y] = y;
    }
  }
  const prodCount = allProds.length;
  const Description = headers[props.name.toLowerCase()] || (() => {});
  const perPageCount = 9;
  const pageCount =
    Math.floor(prodCount / perPageCount) +
    (prodCount % perPageCount === 0 ? 0 : 1);
  const [offset, setOffset] = React.useState(1);
  const handleChange = (e: any, p: any) => {
    setOffset(p);
  };
  if (sort === SORT_ZA) {
    allProds.sort(function (a: any, b: any) {
      return a.title > b.title ? -1 : b.title > a.title ? 1 : 0;
    });
  } else if (sort === SORT_AZ) {
    allProds.sort(function (a: any, b: any) {
      return a.title < b.title ? -1 : b.title < a.title ? 1 : 0;
    });
  } else if (sort === SORT_LH) {
    allProds.sort(function (a: any, b: any) {
      const priceA = Number(a.variants.nodes[0].priceV2.amount);
      const priceB = Number(b.variants.nodes[0].priceV2.amount);
      return priceA < priceB ? -1 : priceB < priceA ? 1 : 0;
    });
  } else if (sort === SORT_HL) {
    allProds.sort(function (a: any, b: any) {
      const priceA = Number(a.variants.nodes[0].priceV2.amount);
      const priceB = Number(b.variants.nodes[0].priceV2.amount);
      return priceA < priceB ? 1 : priceB < priceA ? -1 : 0;
    });
  }
  const prods = allProds.slice(
    (offset - 1) * perPageCount,
    offset * perPageCount
  );

  const hps: string[] = ['All'];
  for (const x of Object.keys(tags)) {
    if (x.endsWith('hp')) {
      hps.push(x);
    }
  }
  hps.sort(function (a: any, b: any) {
    if (a === 'All') {
      return -1;
    }
    if (b === 'All') {
      return 1;
    }
    const na = Number(a.replace('hp', ''));
    const nb = Number(b.replace('hp', ''));
    return na > nb ? 1 : nb > na ? -1 : 0;
  });
  function setHpFilter(hp: string) {
    setHpFilterState(hp);
    setOffset(1);
  }

  function setSort(sort: string) {
    setSortState(sort);
    setOffset(1);
  }

  return (
    <>
      <Box mt={5} mb={3}>
        <Title mt={5}>{props.name.toLowerCase()}</Title>
        <Typography mx={3} mt={2} mb={2} align='center' justifyContent='center'>
          <Description></Description>
        </Typography>
        <If value={tags['Modules']}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            mr={{ sm: 0, md: 4 }}
            alignItems={{ xs: 'center', md: 'flex-end' }}
            justifyContent={{ xs: 'center', md: 'flex-end' }}
          >
            <HpFilter
              hps={hps}
              hpFilter={hpFilter}
              setHpFilter={(s: string) => {
                setHpFilter(s);
              }}
            ></HpFilter>
            <SortFilter
              sort={sort}
              setSort={(s: string) => {
                setSort(s);
              }}
            ></SortFilter>
          </Stack>
        </If>
      </Box>

      <Stack justifyContent='center' alignItems='center' spacing={2} mb={2}>
        <SimpleCollection products={prods}></SimpleCollection>
        <Divider
          sx={{ mt: 1 }}
          flexItem
          color='black'
          role='presentation'
        ></Divider>
        <If value={props.seo !== undefined}>
          <TypeOcrH6>{props.name}</TypeOcrH6>
          <Typography>{props.seo}</Typography>
        </If>
        <If value={pageCount !== 1}>
          <Pagination
            count={pageCount}
            shape='rounded'
            sx={{
              borderRadius: '0',
            }}
            onChange={handleChange}
            page={offset}
          />
        </If>
      </Stack>
    </>
  );
}
